import axios from "axios";

import type {
  TikTokAgeRange,
  TikTokCreatorPick,
  TikTokCreatorSearchCatalog,
  TikTokCreatorSearchFilters,
  TikTokGmvRange,
  TikTokMarketplaceCreator,
  TikTokUnitsSoldRange,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

export const GMV_OPTIONS: Array<{ value: TikTokGmvRange; label: string }> = [
  { value: "GMV_RANGE_0_100", label: "$0–100" },
  { value: "GMV_RANGE_100_1000", label: "$100–1k" },
  { value: "GMV_RANGE_1000_10000", label: "$1k–10k" },
  { value: "GMV_RANGE_10000_AND_ABOVE", label: "$10k+" },
];

export const UNITS_OPTIONS: Array<{ value: TikTokUnitsSoldRange; label: string }> = [
  { value: "UNITS_SOLD_RANGE_0_10", label: "0–10" },
  { value: "UNITS_SOLD_RANGE_10_100", label: "10–100" },
  { value: "UNITS_SOLD_RANGE_100_1000", label: "100–1k" },
  { value: "UNITS_SOLD_RANGE_1000_AND_ABOVE", label: "1k+" },
];

export const AGE_OPTIONS: Array<{ value: TikTokAgeRange; label: string }> = [
  { value: "AGE_RANGE_18_24", label: "18–24" },
  { value: "AGE_RANGE_25_34", label: "25–34" },
  { value: "AGE_RANGE_35_44", label: "35–44" },
  { value: "AGE_RANGE_45_54", label: "45–54" },
  { value: "AGE_RANGE_55_AND_ABOVE", label: "55+" },
];

export const FOLLOWER_PRESETS = [1_000, 10_000, 50_000, 100_000, 500_000];

export const ageLabel = (range: string) => AGE_OPTIONS.find((a) => a.value === range)?.label ?? range;

/** Example searches that prefill the form; they never run on their own. */
export const EXAMPLE_FILTERS: Array<{ label: string; build: (c: TikTokCreatorSearchCatalog) => TikTokCreatorSearchFilters }> = [
  {
    label: "Beauty creators, $1k+ GMV",
    build: (c) => ({
      nicheIds: c.niches.filter((n) => /beauty/i.test(n.name)).map((n) => n.id),
      gmvRanges: ["GMV_RANGE_1000_10000", "GMV_RANGE_10000_AND_ABOVE"],
    }),
  },
  {
    label: "Fast-growing, 10k+ followers",
    build: () => ({ fastGrowing: true, followersMin: 10_000 }),
  },
  {
    label: "Not invited in 90 days",
    build: () => ({ notInvitedLast90Days: true }),
  },
];

export interface FilterChip {
  key: string;
  label: string;
  /** Returns the filters with this chip removed. */
  remove: (f: TikTokCreatorSearchFilters) => TikTokCreatorSearchFilters;
}

const without = (f: TikTokCreatorSearchFilters, ...keys: Array<keyof TikTokCreatorSearchFilters>) => {
  const next = { ...f };
  for (const key of keys) delete next[key];
  return next;
};

/** Active filters as removable chips, in the order the form shows them. */
export function filterChips(f: TikTokCreatorSearchFilters, catalog: TikTokCreatorSearchCatalog | undefined): FilterChip[] {
  const chips: FilterChip[] = [];
  const name = (list: Array<{ id: string; name: string }> | undefined, id: string) =>
    list?.find((o) => o.id === id)?.name ?? id;
  const dropId = (key: "nicheIds" | "languageIds" | "creatorLevelIds" | "categoryIds", id: string) =>
    (prev: TikTokCreatorSearchFilters) => ({ ...prev, [key]: (prev[key] ?? []).filter((x) => x !== id) });

  if (f.keyword) chips.push({ key: "keyword", label: `“${f.keyword}”`, remove: (p) => without(p, "keyword") });
  for (const id of f.nicheIds ?? []) {
    chips.push({ key: `niche-${id}`, label: name(catalog?.niches, id), remove: dropId("nicheIds", id) });
  }
  for (const range of f.gmvRanges ?? []) {
    chips.push({
      key: `gmv-${range}`,
      label: `${GMV_OPTIONS.find((g) => g.value === range)?.label} GMV`,
      remove: (p) => ({ ...p, gmvRanges: (p.gmvRanges ?? []).filter((r) => r !== range) }),
    });
  }
  if (f.followersMin !== undefined || f.followersMax !== undefined) {
    const label =
      f.followersMin !== undefined && f.followersMax !== undefined
        ? `${formatCompactCount(f.followersMin)}–${formatCompactCount(f.followersMax)} followers`
        : f.followersMin !== undefined
          ? `${formatCompactCount(f.followersMin)}+ followers`
          : `Up to ${formatCompactCount(f.followersMax ?? 0)} followers`;
    chips.push({ key: "followers", label, remove: (p) => without(p, "followersMin", "followersMax") });
  }
  for (const id of f.categoryIds ?? []) {
    chips.push({ key: `cat-${id}`, label: catalog?.categoryNames[id] ?? id, remove: dropId("categoryIds", id) });
  }
  if (f.fastGrowing) chips.push({ key: "fast", label: "Fast growing", remove: (p) => without(p, "fastGrowing") });
  if (f.notInvitedLast90Days) {
    chips.push({ key: "notInvited", label: "Not invited in 90 days", remove: (p) => without(p, "notInvitedLast90Days") });
  }
  if (f.gender) {
    const who = f.gender === "FEMALE" ? "women" : "men";
    chips.push({
      key: "gender",
      label: f.genderMinPercent ? `${f.genderMinPercent}%+ ${who}` : `Mostly ${who}`,
      remove: (p) => without(p, "gender", "genderMinPercent"),
    });
  }
  for (const range of f.ageRanges ?? []) {
    chips.push({
      key: `age-${range}`,
      label: `Ages ${ageLabel(range)}`,
      remove: (p) => ({ ...p, ageRanges: (p.ageRanges ?? []).filter((r) => r !== range) }),
    });
  }
  for (const id of f.languageIds ?? []) {
    chips.push({ key: `lang-${id}`, label: name(catalog?.languages, id), remove: dropId("languageIds", id) });
  }
  for (const id of f.creatorLevelIds ?? []) {
    chips.push({ key: `lvl-${id}`, label: name(catalog?.creatorLevels, id), remove: dropId("creatorLevelIds", id) });
  }
  if (f.minAvgVideoViews !== undefined) {
    chips.push({ key: "views", label: `${formatCompactCount(f.minAvgVideoViews)}+ avg views`, remove: (p) => without(p, "minAvgVideoViews") });
  }
  if (f.minEngagementRate !== undefined) {
    chips.push({ key: "eng", label: `${f.minEngagementRate}%+ engagement`, remove: (p) => without(p, "minEngagementRate") });
  }
  if (f.minLiveViewers !== undefined) {
    chips.push({ key: "live", label: `${formatCompactCount(f.minLiveViewers)}+ LIVE viewers`, remove: (p) => without(p, "minLiveViewers") });
  }
  for (const range of f.unitsSoldRanges ?? []) {
    chips.push({
      key: `units-${range}`,
      label: `${UNITS_OPTIONS.find((u) => u.value === range)?.label} units sold`,
      remove: (p) => ({ ...p, unitsSoldRanges: (p.unitsSoldRanges ?? []).filter((r) => r !== range) }),
    });
  }
  if (f.maxCommissionRate) {
    chips.push({ key: "comm", label: `Commission under ${f.maxCommissionRate}%`, remove: (p) => without(p, "maxCommissionRate") });
  }
  if (f.postRate) {
    const label = { OK: "OK", GOOD: "Good", BETTER: "Better" }[f.postRate];
    chips.push({ key: "post", label: `Posts after sample: ${label}+`, remove: (p) => without(p, "postRate") });
  }
  if (f.agencyStatus) {
    chips.push({
      key: "agency",
      label: f.agencyStatus === "AGENCY_MANAGED" ? "Agency managed" : "Independent",
      remove: (p) => without(p, "agencyStatus"),
    });
  }
  return chips;
}

/** Drops empty values so the form state and the request stay small. */
export function compactFilters(f: TikTokCreatorSearchFilters): TikTokCreatorSearchFilters {
  const out: TikTokCreatorSearchFilters = {};
  for (const [key, value] of Object.entries(f) as Array<[keyof TikTokCreatorSearchFilters, unknown]>) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    (out as Record<string, unknown>)[key] = typeof value === "string" ? value.trim() || undefined : value;
    if (out[key] === undefined) delete out[key];
  }
  return out;
}

const usd = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: amount >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: amount >= 10_000 ? 1 : 0,
  }).format(amount);

export function formatMoney(value: { amount: number; currency: string } | null): string {
  if (!value) return "—";
  return value.currency === "USD" ? usd(value.amount) : `${value.amount.toLocaleString()} ${value.currency}`;
}

/** Sort key for the GMV column: a band sorts by its floor, just under an exact amount. */
export function gmvSortValue(c: TikTokMarketplaceCreator): number {
  if (c.gmv) return c.gmv.amount;
  if (c.gmvRange) return c.gmvRange.min - 0.5;
  return -1;
}

export type CreatorSearchBlock =
  | { kind: "not_connected" }
  | { kind: "reconnect" }
  | { kind: "awaiting_approval" }
  | { kind: "error"; message: string };

/** Turns the filter-catalogue failure into the page's blocking state. */
export function blockFromError(error: unknown): CreatorSearchBlock {
  if (axios.isAxiosError(error)) {
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (code === "TIKTOK_SHOP_NOT_CONNECTED") return { kind: "not_connected" };
    if (code === "INTEGRATION_RECONNECT_REQUIRED") return { kind: "reconnect" };
    if (code === "TIKTOK_CREATOR_SEARCH_SCOPE_MISSING") return { kind: "awaiting_approval" };
  }
  return { kind: "error", message: errorMessage(error) };
}

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.message)) return data.message.join(", ");
    return error.message || "Something went wrong.";
  }
  return "Something went wrong.";
}

/** Seconds to wait when the search was rate limited, else null. */
export function retryAfterSeconds(error: unknown): number | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null;
  const body = (error.response.data as { retryAfterSec?: number } | undefined)?.retryAfterSec;
  const header = Number(error.response.headers?.["retry-after"]);
  const seconds = body ?? (Number.isFinite(header) ? header : 60);
  return Math.max(1, Math.round(seconds));
}

export function formatCountdown(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
  }
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** What the TikTok Shop page needs to start an invite or a conversation with this creator. */
export function creatorPick(creator: TikTokMarketplaceCreator): TikTokCreatorPick {
  return {
    openId: creator.openId,
    username: creator.username,
    nickname: creator.nickname,
    avatarUrl: creator.avatarUrl,
    followerCount: creator.followers,
  };
}
